import type { UniversalModifierValue } from "schemas/generated/fileModifiers";

import React from "react";

import ProductSelectorModifier from "./base/ProductSelectorModifier";
import { useSeletedElementDesignations } from "./store/ModifiersStoreContext";

interface ModifierRowProps {
  title: string;
  children: React.ReactNode;
}

const ModifierRow: React.FC<ModifierRowProps> = ({ title, children }) => {
  return (
    <div className="grid grid-cols-[80px_1fr] gap-3 items-start">
      <span className="text-sm font-medium text-default h-[24px] flex flex-col justify-center">
        {title}
      </span>
      {children}
    </div>
  );
};

interface ModifierSectionProps {
  title?: string;
  rows: {
    title: string;
    component: React.ReactNode;
  }[];
  showSeparator?: boolean;
}

const ModifierSection: React.FC<ModifierSectionProps> = ({
  title,
  rows,
  showSeparator = false,
}) => {
  return (
    <div className="flex flex-col gap-3">
      {title && (
        <>
          <div className="text-sm font-medium text-default">{title}</div>
          <div className="border-t-0.5 border-border -mx-4" />
        </>
      )}
      {showSeparator && !title && (
        <div className="border-t-0.5 border-border" />
      )}
      <div className="flex flex-col gap-3">
        {rows.map((row) => (
          <ModifierRow key={row.title} title={row.title}>
            {row.component}
          </ModifierRow>
        ))}
      </div>
    </div>
  );
};

type ModifierSectionType = {
  title?: string;
  rows: {
    title: string;
    component: React.ReactNode;
  }[];
};

type ModifierLayoutProps = {
  sections: ModifierSectionType[];
  onModifiersValueChange: (
    modifiers: UniversalModifierValue[],
    byteOffset: number,
  ) => void;
};

const ModifierLayout: React.FC<ModifierLayoutProps> = ({
  sections,
  onModifiersValueChange,
}) => {
  const extraSections: ModifierSectionType[] = [];
  const selectedDesignations = useSeletedElementDesignations();

  const productDesignation = selectedDesignations?.find(
    (designation) =>
      designation.type === "dynamicDataSource" &&
      designation.data.type === "product",
  );
  if (productDesignation) {
    extraSections.push({
      rows: [
        {
          title: "Product",
          component: (
            <ProductSelectorModifier
              onChange={(modifiers) =>
                onModifiersValueChange(
                  modifiers,
                  productDesignation.element.byteOffset,
                )
              }
            />
          ),
        },
      ],
    });
  }

  return (
    <div className="flex flex-col gap-4">
      {[...extraSections, ...sections].map((section, index) => (
        <ModifierSection
          key={section.title ?? index}
          title={section.title}
          rows={section.rows}
          showSeparator={index > 0}
        />
      ))}
    </div>
  );
};

export default ModifierLayout;
